import API from '@/services/API'
import {
  PlatformReportGroup,
  UpdateReportGroupRequestBody,
  UpdateReportGroupResponse
} from '@betterboards/shared/types/Report'
import { PlatformQuestionnaireVariant } from '@/types/Platform'
import { listFullReportGroupsBySurveyGroup } from '@/graphql/custom/listFullReportGroupsBySurveyGroup'
import { GraphQLResult } from '@aws-amplify/api-graphql'
import { ListReportGroupsBySurveyGroupQuery } from '@/API'

export function getDefaultReportGroup (reportGroups: PlatformReportGroup[]): PlatformReportGroup {
  return reportGroups.reduce((selectedReportGroup: PlatformReportGroup, reportGroup: PlatformReportGroup): PlatformReportGroup => {
    if (selectedReportGroup.createdAt.localeCompare(reportGroup.createdAt) < 0) {
      return selectedReportGroup
    }
    return reportGroup
  })
}

export async function createReportGroup (
  surveyGroupId: string,
  name: string,
  variants: PlatformQuestionnaireVariant[]
): Promise<void> {
  const payload = {
    surveyGroupId,
    name: name.trim(),
    variants: variants.map(({ name, variantCode }: PlatformQuestionnaireVariant) => ({
      name: name.trim(),
      variantCode: variantCode
    }))
  }

  console.debug('Creating ReportGroup with payload:', payload)
  let response
  try {
    response = await API.post('backendfunctions', '/report/group', {
      body: payload
    })
  } catch (err) {
    console.error('Failed to create ReportGroup', JSON.stringify({ payload, err }, null, 2))
    throw err
  }

  return response
}

export async function updateReportGroup (
  reportGroupData: UpdateReportGroupRequestBody
): Promise<UpdateReportGroupResponse> {
  if (!reportGroupData.reportGroupId) {
    throw new Error('Failed to update ReportGroup as no ReportGroup ID was provided.')
  }
  const payload = {
    reportGroupId: reportGroupData.reportGroupId,
    surveyGroupId: reportGroupData.surveyGroupId,
    scores: reportGroupData.scores,
    quartiles: reportGroupData.quartiles,
    comparisonSurveyGroupId: reportGroupData.comparisonSurveyGroupId
  }

  console.debug('Updating ReportGroup/Reports with payload:', payload)
  let response
  try {
    response = await API.put('backendfunctions', '/report/group', {
      body: payload
    })
  } catch (err) {
    console.error('Failed to update ReportGroup/Reports', JSON.stringify({ payload, err }, null, 2))
    throw err
  }

  return response
}

export async function fetchReportGroupBySurveyGroupId (surveyGroupId: string, companyId: string): Promise<PlatformReportGroup | undefined> {
  if (!companyId) {
    throw new Error('Failed to load report as no company is selected.')
  }
  const filter = {
    companyId: {
      eq: companyId
    }
  }
  const reportGroupsResponse = await API.graphql({
    query: listFullReportGroupsBySurveyGroup,
    variables: {
      surveyGroupId,
      filter
    }
  }) as GraphQLResult<ListReportGroupsBySurveyGroupQuery>

  const reportGroups: PlatformReportGroup[] | undefined = reportGroupsResponse.data?.listReportGroupsBySurveyGroup?.items as PlatformReportGroup[] | undefined
  if (!reportGroups?.length) {
    console.error('Failed to find ReportGroups', reportGroupsResponse)
    throw new Error('Failed to fetch Report Groups.')
  }

  const defaultReportGroup: PlatformReportGroup = getDefaultReportGroup(reportGroups)

  return {
    ...defaultReportGroup,
    quartiles: typeof defaultReportGroup.quartiles === 'string' ? JSON.parse(defaultReportGroup.quartiles) : defaultReportGroup.quartiles
  }
}
